import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api';
import { SearchBarCore } from '../components/SearchBarCore';
import { Shell } from '../components/Shell';
import './SearchPage.css';

type SearchRes = {
  modules: Array<{
    slug: string;
    title: string;
    summary: string | null;
    topic_family?: string | null;
    sensitivity?: number;
    matchIn?: 'title' | 'summary' | 'both';
  }>;
  journeys: Array<{ slug: string; title: string; description?: string | null; step_count?: number }>;
  topics: Array<{ slug: string; title: string; family?: string | null; summary?: string | null }>;
  comparePages: Array<{ slug: string; title: string; summary?: string | null }>;
  query: string;
};

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  const [data, setData] = useState<SearchRes | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setDraft(q);
  }, [q]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setData(null);
      setErr(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson<SearchRes>(`/api/search?q=${encodeURIComponent(q)}`);
        if (!cancelled) {
          setData(res);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Search failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q]);

  const total = useMemo(() => {
    if (!data) return 0;
    return (
      data.modules.length +
      data.journeys.length +
      (data.topics?.length ?? 0) +
      (data.comparePages?.length ?? 0)
    );
  }, [data]);

  return (
    <Shell>
      <div className="wi-search-page">
        <header className="wi-search-header">
          <div className="wi-search-hero">
            <p className="wi-search-kicker">Library</p>
            <h1>Search</h1>
            <p className="wi-search-lede muted">
              Modules, journeys, compare pages, and themes in one place. Match hints show whether
              your words appeared in the title, summary, or both.
            </p>
            <div className="wi-search-bar-wrap">
              <SearchBarCore
                variant="page"
                value={draft}
                onValueChange={setDraft}
                placeholder="Search modules, journeys, compare pages, topics..."
                className="wi-search-inline"
                inputId="wi-search-page-input"
              />
            </div>
            {q ? (
              <p className="wi-search-query">
                <span className="wi-search-query-q">"{q}"</span>
                <span className="wi-search-query-meta">
                  {total} result{total === 1 ? '' : 's'}
                </span>
              </p>
            ) : (
              <p className="wi-search-hint muted">Enter at least two characters to search.</p>
            )}
          </div>
        </header>
        {err ? <p className="wi-search-err">{err}</p> : null}
        {data && q ? (
          <div className="wi-search-cols">
            <section>
              <h2>Modules</h2>
              {data.modules.length ? (
                <ul className="wi-search-list">
                  {data.modules.map((m) => (
                    <li key={m.slug}>
                      <Link to={`/m/${m.slug}`}>{m.title}</Link>
                      {m.matchIn ? (
                        <span className="wi-search-match" title="Where the query matched">
                          Matched: {m.matchIn === 'both' ? 'title & summary' : m.matchIn}
                        </span>
                      ) : null}
                      {m.topic_family ? <span className="wi-search-tag">{m.topic_family}</span> : null}
                      {m.summary ? <p>{m.summary}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No modules matched.</p>
              )}
            </section>
            <section>
              <h2>Journeys</h2>
              {data.journeys.length ? (
                <ul className="wi-search-list">
                  {data.journeys.map((j) => (
                    <li key={j.slug}>
                      <Link to={`/journeys/${j.slug}`}>{j.title}</Link>
                      {j.description ? <p>{j.description}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No journeys matched.</p>
              )}
            </section>
            <section>
              <h2>Compare Pages</h2>
              {data.comparePages?.length ? (
                <ul className="wi-search-list">
                  {data.comparePages.map((page) => (
                    <li key={page.slug}>
                      <Link to={`/compare/${page.slug}`}>{page.title}</Link>
                      {page.summary ? <p>{page.summary}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No compare pages matched.</p>
              )}
            </section>
            <section>
              <h2>Topics</h2>
              {data.topics?.length ? (
                <ul className="wi-search-list">
                  {data.topics.map((t) => (
                    <li key={t.slug}>
                      <Link to="/browse">
                        {t.title}
                        {t.family ? <span className="wi-search-tag">{t.family}</span> : null}
                      </Link>
                      {t.summary ? <p>{t.summary}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No topics matched.</p>
              )}
            </section>
          </div>
        ) : null}
      </div>
    </Shell>
  );
}
